/**
 * Bucket rebalance math — mirrors `src-tauri/crates/chorepop-core/src/bucket_math.rs`.
 *
 * Supports US-BUCKET-03 (add), US-BUCKET-04 (edit), US-BUCKET-05/-12
 * (deactivate), US-BUCKET-07/-08 (child add/edit).
 *
 * Pure function only — no DB, no IO. Must produce identical results to
 * the Rust mirror for any given input. The Rust version is the source of
 * truth for production; this TS version runs in the dev mock and in any
 * future client-side preview of the rebalance (e.g. showing "Spend will
 * go from 35% → 25%" while the guardian fills in the form).
 *
 * See USER-STORIES.md US-BUCKET-03 for the fixed/flexible model.
 */
import { BPS_TOTAL } from './money';

export interface ExistingBucket {
  bucketId: number;
  allocationBps: number;
  percentIsFixed: boolean;
  /** Visual order (lower = displayed earlier). Secondary tiebreaker in remainder distribution. */
  sortOrder: number;
  /** Rounding priority (higher = more likely to receive the ceiling value on uneven splits). */
  priority: number;
}

export interface RebalanceResult {
  bucketId: number;
  newAllocationBps: number;
}

export type RebalanceError =
  | { kind: 'newBucketBpsOutOfRange'; bps: number }
  | { kind: 'currentAllocationsDoNotSumTo10000'; actual: number }
  | { kind: 'noFlexibleBuckets' }
  | { kind: 'flexiblePoolTooSmall'; availableBps: number; requestedBps: number };

export function formatRebalanceError(err: RebalanceError): string {
  switch (err.kind) {
    case 'newBucketBpsOutOfRange':
      return `new bucket percentage must be in 1..9999 bps (got ${err.bps})`;
    case 'currentAllocationsDoNotSumTo10000':
      return `current bucket allocations must sum to 10000 bps (got ${err.actual})`;
    case 'noFlexibleBuckets':
      return (
        "no flexible buckets available to absorb the new bucket's percentage; " +
        'edit an existing bucket first (set its percent_is_fixed = false)'
      );
    case 'flexiblePoolTooSmall':
      return `flexible pool is too small: requested ${err.requestedBps} bps from ${err.availableBps} bps of flexible allocation; reduce a fixed bucket's percentage first (US-BUCKET-04)`;
  }
}

type Ok = { ok: true; value: RebalanceResult[] };
type Err = { ok: false; error: RebalanceError };
export type RebalanceOutcome = Ok | Err;

/**
 * Compute how the flexible buckets shrink to make room for a new bucket
 * at `newBucketBps`. Returns one entry per flexible bucket (in input
 * order); fixed buckets are not included — they do not change.
 *
 * Returns a tagged result rather than throwing so callers can present
 * structured error messages (the form needs to distinguish "too small
 * pool" from "no flexible buckets" to give different guidance).
 */
export function rebalanceForNewBucket(
  existing: ReadonlyArray<ExistingBucket>,
  newBucketBps: number,
): RebalanceOutcome {
  if (newBucketBps <= 0 || newBucketBps >= BPS_TOTAL) {
    return { ok: false, error: { kind: 'newBucketBpsOutOfRange', bps: newBucketBps } };
  }

  const currentTotal = existing.reduce((acc, b) => acc + b.allocationBps, 0);
  if (currentTotal !== BPS_TOTAL) {
    return {
      ok: false,
      error: { kind: 'currentAllocationsDoNotSumTo10000', actual: currentTotal },
    };
  }

  const flexible = existing.filter((b) => !b.percentIsFixed);
  if (flexible.length === 0) {
    return { ok: false, error: { kind: 'noFlexibleBuckets' } };
  }

  const flexibleTotal = flexible.reduce((acc, b) => acc + b.allocationBps, 0);
  if (flexibleTotal < newBucketBps) {
    return {
      ok: false,
      error: {
        kind: 'flexiblePoolTooSmall',
        availableBps: flexibleTotal,
        requestedBps: newBucketBps,
      },
    };
  }

  const targetFlexibleTotal = flexibleTotal - newBucketBps;

  // Integer-floor allocation plus largest-remainder distribution.
  // Matches the Rust ordering: (remainder desc, sortOrder asc, bucketId asc).
  interface Entry {
    bucketId: number;
    sortOrder: number;
    priority: number;
    floor: number;
    remainderNum: number;
    originalIndex: number;
  }

  const entries: Entry[] = flexible.map((b, i) => {
    const product = b.allocationBps * targetFlexibleTotal;
    const floor = Math.floor(product / flexibleTotal);
    const remainderNum = product - floor * flexibleTotal;
    return {
      bucketId: b.bucketId,
      sortOrder: b.sortOrder,
      priority: b.priority,
      floor,
      remainderNum,
      originalIndex: i,
    };
  });

  const floorSum = entries.reduce((acc, e) => acc + e.floor, 0);
  const leftover = targetFlexibleTotal - floorSum;

  const ranking = [...entries].sort((a, b) => {
    if (b.remainderNum !== a.remainderNum) return b.remainderNum - a.remainderNum;
    // Largest remainder first; ties broken by priority DESC, then sortOrder ASC, then bucketId ASC.
    if (b.priority !== a.priority) return b.priority - a.priority;
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.bucketId - b.bucketId;
  });

  for (let i = 0; i < leftover; i++) {
    const rankedEntry = ranking[i];
    /* c8 ignore next -- defensive: i < leftover ≤ entries.length, so ranking[i] is always defined */
    if (!rankedEntry) continue;
    const entry = entries[rankedEntry.originalIndex];
    /* c8 ignore next -- defensive: originalIndex is always a valid index into entries */
    if (!entry) continue;
    entry.floor += 1;
  }

  return {
    ok: true,
    value: entries.map((e) => ({
      bucketId: e.bucketId,
      newAllocationBps: e.floor,
    })),
  };
}

/**
 * Mirror of Rust `rebalance_for_edited_bucket`. Used by the live preview
 * on `EditBucketScreen` so users see the exact same rebalance outcome
 * the backend will compute. See US-BUCKET-04.
 */
export function rebalanceForEditedBucket(
  existing: ReadonlyArray<ExistingBucket>,
  editedBucketId: number,
  newAllocationBps: number,
): RebalanceOutcome {
  if (newAllocationBps <= 0 || newAllocationBps >= BPS_TOTAL) {
    return { ok: false, error: { kind: 'newBucketBpsOutOfRange', bps: newAllocationBps } };
  }
  const currentTotal = existing.reduce((acc, b) => acc + b.allocationBps, 0);
  if (currentTotal !== BPS_TOTAL) {
    return {
      ok: false,
      error: { kind: 'currentAllocationsDoNotSumTo10000', actual: currentTotal },
    };
  }

  // Confirm the edited bucket is actually in the list. We don't read
  // its fields — the partition below uses the OTHER buckets' flags.
  const exists = existing.some((b) => b.bucketId === editedBucketId);
  /* c8 ignore start -- defensive: caller guarantees editedBucketId ∈ existing */
  if (!exists) {
    return {
      ok: false,
      error: { kind: 'currentAllocationsDoNotSumTo10000', actual: currentTotal },
    };
  }
  /* c8 ignore stop */

  const othersFlex = existing.filter((b) => b.bucketId !== editedBucketId && !b.percentIsFixed);
  const othersFlexTotal = othersFlex.reduce((a, b) => a + b.allocationBps, 0);
  const othersFixedTotal = existing
    .filter((b) => b.bucketId !== editedBucketId && b.percentIsFixed)
    .reduce((a, b) => a + b.allocationBps, 0);

  const targetOthersFlexTotal = BPS_TOTAL - newAllocationBps - othersFixedTotal;

  // Short-circuit: pure flag flip or a percent edit whose net delta
  // happens to be zero. No OTHER bucket needs updating.
  if (targetOthersFlexTotal === othersFlexTotal) {
    return { ok: true, value: [] };
  }

  if (othersFlex.length === 0) {
    return { ok: false, error: { kind: 'noFlexibleBuckets' } };
  }
  if (targetOthersFlexTotal < 0) {
    return {
      ok: false,
      error: {
        kind: 'flexiblePoolTooSmall',
        availableBps: BPS_TOTAL - othersFixedTotal,
        requestedBps: newAllocationBps,
      },
    };
  }

  interface Entry {
    bucketId: number;
    sortOrder: number;
    priority: number;
    floor: number;
    remainderNum: number;
    originalIndex: number;
  }

  let entries: Entry[];
  if (othersFlexTotal === 0) {
    // Zero denominator — split target evenly (matches Rust behavior).
    const n = othersFlex.length;
    const base = Math.floor(targetOthersFlexTotal / n);
    entries = othersFlex.map((b, i) => ({
      bucketId: b.bucketId,
      sortOrder: b.sortOrder,
      priority: b.priority,
      floor: base,
      remainderNum: 0,
      originalIndex: i,
    }));
  } else {
    entries = othersFlex.map((b, i) => {
      const product = b.allocationBps * targetOthersFlexTotal;
      const floor = Math.floor(product / othersFlexTotal);
      const remainderNum = product - floor * othersFlexTotal;
      return {
        bucketId: b.bucketId,
        sortOrder: b.sortOrder,
        priority: b.priority,
        floor,
        remainderNum,
        originalIndex: i,
      };
    });
  }

  const floorSum = entries.reduce((acc, e) => acc + e.floor, 0);
  const leftover = targetOthersFlexTotal - floorSum;

  const ranking = [...entries].sort((a, b) => {
    if (b.remainderNum !== a.remainderNum) return b.remainderNum - a.remainderNum;
    // Largest remainder first; ties broken by priority DESC, then sortOrder ASC, then bucketId ASC.
    if (b.priority !== a.priority) return b.priority - a.priority;
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.bucketId - b.bucketId;
  });

  for (let i = 0; i < leftover; i++) {
    const rankedEntry = ranking[i];
    /* c8 ignore next -- defensive: i < leftover ≤ entries.length, so ranking[i] is always defined */
    if (!rankedEntry) continue;
    const entry = entries[rankedEntry.originalIndex];
    /* c8 ignore next -- defensive: originalIndex is always a valid index into entries */
    if (!entry) continue;
    entry.floor += 1;
  }

  return {
    ok: true,
    value: entries.map((e) => ({
      bucketId: e.bucketId,
      newAllocationBps: e.floor,
    })),
  };
}
